import { type FunctionComponent } from 'preact'
import { useSignal, batch } from '@preact/signals'
import { html } from 'htm/preact'
import { SubstrateInput } from '@substrate-system/input'
import { SubstrateButton } from '@substrate-system/button'
import ky from 'ky'
import './notify-form.css'

type NotifyStatus = 'idle'|'submitting'|'success'|'error'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export const NotifyForm:FunctionComponent = function NotifyForm () {
    const email = useSignal('')
    const status = useSignal<NotifyStatus>('idle')
    const errorMessage = useSignal('')

    const handleSubmit = async (event:Event) => {
        event.preventDefault()
        const value = email.value.trim()

        if (!EMAIL_RE.test(value)) {
            batch(() => {
                status.value = 'error'
                errorMessage.value = 'Enter a valid email address.'
            })
            return
        }

        batch(() => {
            status.value = 'submitting'
            errorMessage.value = ''
        })

        try {
            await ky.post('/api/notify', { json: { email: value } })
            status.value = 'success'
        } catch (_err) {
            batch(() => {
                status.value = 'error'
                errorMessage.value =
                    'Something went wrong. Please try again.'
            })
        }
    }

    if (status.value === 'success') {
        return html`<div class="notify-form notify-form-success">
            <p class="notify-success-message">
                Thanks. We will email you when we launch.
            </p>
        </div>`
    }

    return html`<form
        class="notify-form"
        onSubmit=${handleSubmit}
        novalidate
    >
        <h2 class="notify-heading">
            Get updated about product launch
        </h2>
        <p>
            We are currently in a closed alpha stage.
            Add your email address here to be notified about the launch
            and beta testing release, or use <a href="/tools">the
            free tools</a>
        </p>
        <div class="notify-controls">
            <${SubstrateInput.TAG}
                id="notification-input"
                class="notify-input"
                label="Email address"
                name="email"
                type="email"
                inputmode="email"
                autocomplete="email"
                placeholder="you@company.com"
                required
                aria-invalid=${status.value === 'error' ? 'true' : 'false'}
                value=${email.value}
                onInput=${(event:InputEvent) => {
                    email.value =
                        (event.target as HTMLInputElement).value
                }}
            ><//>
            <${SubstrateButton.TAG}
                class="notify-submit"
                type="submit"
                spinning=${status.value === 'submitting'}
            >
                Notify me
            <//>
        </div>
        ${status.value === 'error' ?
            html`<p class="notify-error" role="alert">
                ${errorMessage.value}
            </p>` :
            null}
    </form>`
}
